<template>
	<div class="report-form">
		<div class="report-form-row-2">
			<ja-dropdown
				v-model="simulationType"
				required
				:placeholder="$t('simTypePlaceholder')"
				:label="$t('simulationTypeLbl')"
				:options="simulationTypes"
				name="simulationType"
			></ja-dropdown>
			<ja-dropdown
				v-model="lifeStage"
				required
				:placeholder="$t('lifestagePlaceholder')"
				:label="$t('lifeStage')"
				:options="lifeStages"
				:disabled="disableLifeStageSelection"
				name="lifeStage"
			></ja-dropdown>
		</div>
		<div class="report-form-row-2">
			<ja-input
				v-model="schoolInput"
				name="school"
				type="text"
				:label="$t('schoolName')"
				:placeholder="$t('schoolNamePlaceholder')"
				:disabled="facility < 0"
				escape-string
				trim-spaces
				:autocomplete-options="schools"
				@optionSelected="onSchoolSelected($event, { loadClasses: true })"
			></ja-input>
			<ja-input
				v-model="educatorInput"
				name="simEducator"
				:disabled="facility < 0 || !!educatorName"
				:label="$t('educatorName')"
				:placeholder="
					educatorName ? educatorName : $t('educatorNamePlaceholder')
				"
				:autocomplete-options="educators"
				escape-string
				trim-spaces
				@optionSelected="onEducatorSelected($event, { loadClasses: true })"
			></ja-input>
		</div>
		<div class="report-form-row-2">
			<ja-dropdown
				v-model="simulationClass"
				:disabled="isClassFieldDisabled"
				:placeholder="$t('classNamePlaceholder')"
				:label="$t('className')"
				:options="simulationClasses"
				name="simulationClass"
			></ja-dropdown>
		</div>
	</div>
</template>
<script>
import ReportingMixin from '@/mixins/ReportingMixin';
import AdminFacility from '@/mixins/AdminFacility';
import Input from '@/components/ui/form/Input';
import Dropdown from '@/components/ui/form/Dropdown';
export default {
	components: {
		jaInput: Input,
		jaDropdown: Dropdown
	},
	mixins: [ReportingMixin, AdminFacility],
	props: {
		filters: {
			type: Object,
			default: null
		},
		simulationTypes: {
			type: Array,
			default: () => {
				return [];
			}
		},
		lifeStages: {
			type: Array,
			default: () => {
				return [];
			}
		},
		educatorName: {
			type: String,
			default: ''
		},
		clear: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			lifeStage: this.getInitialLifeStage(),
			disableLifeStageSelection: false
		};
	},
	computed: {
		activeFilters() {
			if (this.filters) {
				return this.filters;
			}

			return {
				facility: {
					label: this.$t('facilityFilter')
				},
				simulationType: {
					label: this.$t('simTypeFilter')
				},
				school: {
					label: this.$t('schoolFilter')
				},
				educator: {
					label: this.$t('educatorFilter')
				},
				simulationClass: {
					label: this.$t('classFilter')
				},
				lifeStage: {
					label: this.$t('lifeStageFilter')
				}
			};
		},
		isClassFieldDisabled() {
			if (this.selectedFacilityId < 0) {
				return true;
			}
			if ((this.school || this.educator) && this.simulationClasses.length) {
				return false;
			}

			return true;
		},
		requiredFieldsPopulated() {
			return (
				this.selectedFacilityId >= 0 &&
				this.simulationType >= 0 &&
				this.lifeStage >= 0
			);
		}
	},

	watch: {
		clear(value) {
			if (value === true) {
				this.facilityChanged();
			}
		},
		selectedFacility() {
			if (this.school || this.educator) {
				this.getSimulationClasses();
			}
		},
		simulationType(newValue) {
			if (newValue >= 0) {
				const obj = {};
				obj.name = this.simulationTypes[newValue].name;
				obj.value = this.simulationTypes[newValue].value;
				this.activeFilters.simulationType = obj;
				this.setLifeStageBySimType(this.activeFilters.simulationType.name);
			} else {
				this.activeFilters.simulationType = null;
			}
			this.$emit('filtersUpdated', this.activeFilters);
		},
		school(newValue) {
			this.activeFilters.school.name = newValue;
			this.activeFilters.school.value = newValue;

			this.simulationClass = -1;

			this.$emit('filtersUpdated', this.activeFilters);
			if (this.facility >= 0) {
				this.getSimulationClasses();
			}
		},
		lifeStage(newValue) {
			if (newValue >= 0) {
				const obj = {};
				obj.name = this.lifeStages[newValue].name;
				obj.value = this.lifeStages[newValue].value;
				this.activeFilters.lifeStage = obj;
			} else {
				this.activeFilters.lifeStage = null;
			}

			this.$emit('filtersUpdated', this.activeFilters);
		},
		educator(newValue) {
			this.activeFilters.educator.name = newValue;
			this.activeFilters.educator.value = newValue;

			this.$emit('filtersUpdated', this.activeFilters);
			if (this.facility >= 0) {
				this.getSimulationClasses();
			}
		},
		simulationClass(newValue) {
			if (newValue >= 0) {
				this.activeFilters.simulationClass.name = this.simulationClasses[
					newValue
				].name;
				this.activeFilters.simulationClass.value = this.simulationClasses[
					newValue
				].value;
			} else {
				this.activeFilters.simulationClass.name = null;
				this.activeFilters.simulationClass.value = null;
			}

			this.$emit('filtersUpdated', this.activeFilters);
		}
	},
	mounted() {
		this.setLifeStageBySimType(this.activeFilters.simulationType.name);
	},
	created() {
		if (this.educatorName) {
			this.educator = this.educatorName;
		}
	},
	methods: {
		facilityChanged() {
			this.simulationType = -1;
			this.lifeStage = -1;
			this.schoolInput = '';
			this.educatorInput = '';
			if (this.simulationClass >= 0) {
				this.simulationClass = -1;
			}

			this.educators = [];
			this.schools = [];
		},
		getInitialLifeStage() {
			if (this.filters && this.filters.lifeStage) {
				return this.lifeStages.findIndex(
					lifeStage => lifeStage.value == this.filters.lifeStage.value
				);
			}
			return -1;
		},
		setLifeStageBySimType(simType) {
			if (simType && simType.toLowerCase().includes('entry')) {
				this.lifeStage = this.lifeStages.findIndex(lifeStage => {
					return lifeStage.name == 'Young Adult';
				});
				this.disableLifeStageSelection = true;
			} else {
				this.disableLifeStageSelection = false;
			}
		}
	}
};
</script>
